<template>
  <v-form ref="form" v-model="valid" lazy-validation @submit.prevent="onCreate">
    <v-card tile>
      <v-card-title class="grey lighten-2">Länka avvikelse</v-card-title>

      <v-card-text class="mt-2">
        <v-row>
          <v-col>
            <v-card class="d-flex pa-4">
              <div class="flex-grow-1">
                <div class="text--secondary">Tågnr.</div>
                <div class="text-subtitle-1 font-weight-bold">
                  {{ crew.train_number }}
                </div>
              </div>
              <div class="flex-grow-1">
                <div class="text--secondary">Tjänstedatum</div>
                <div class="text-subtitle-1 font-weight-bold">
                  {{ crew.duty_date }}
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-autocomplete
              :items="stops"
              item-value="short_name"
              :item-text="item => `(${item.short_name}) ${item.stop_name}`"
              :filter="
                (item, query) =>
                  item.short_name.toLowerCase().includes(query.toLowerCase()) ||
                  item.stop_name.toLowerCase().includes(query.toLowerCase())
              "
              outlined
              label="Från plats"
              v-model="from_place"
              :rules="[required]"
            />
          </v-col>
          <v-col>
            <v-autocomplete
              :items="stops"
              item-value="short_name"
              :item-text="item => `(${item.short_name}) ${item.stop_name}`"
              :filter="
                (item, query) =>
                  item.short_name.toLowerCase().includes(query.toLowerCase()) ||
                  item.stop_name.toLowerCase().includes(query.toLowerCase())
              "
              outlined
              ref="routeEndPlace"
              label="Till plats"
              v-model="to_place"
              :rules="[required]"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-autocomplete
              :items="groupedEvents"
              :loading="$apollo.loading"
              :rules="[required]"
              item-value="uuid"
              item-text="title"
              outlined
              placeholder="Välj händelse att länka på..."
              label="Händelser"
              v-model="model.eventUUID"
            />
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer />
        <v-btn rounded @click="closeModal">Avbryt</v-btn>
        <v-btn rounded type="submit" color="primary" :loading="pending"
          >Länka</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-form>
</template>

<script>
import gql from 'graphql-tag'
import { required } from '@/common/validationRules'
import { get, sync } from 'vuex-pathify'
import * as uuid from 'uuid/v4'
import { addDays, format } from 'date-fns'

export default {
  name: 'LinkDeviationCrewModal',

  props: {
    item: Object
  },

  data: () => ({
    required,
    valid: true,
    pending: false,
    model: {
      eventUUID: null,
      delayReasonSlug: null
    }
  }),

  created() {
    this.$store.set('crew/model', {
      uuid: uuid(),
      duty_date: this.item.duty_date,
      train_number: this.item.train_number,
      from: this.item.from || null,
      to: this.item.to || null
    })
  },

  apollo: {
    events: {
      query: gql`
        query events($filter: GetEventsInput!) {
          events(filter: $filter) {
            uuid
            title
          }
        }
      `,
      fetchPolicy: 'no-cache',
      variables() {
        return {
          filter: {
            archived: true,
            district_id: this.$store.get('app/selectedDistrictId'),
            from: format(
              addDays(new Date(this.item.duty_date), -5),
              'yyyy-MM-dd'
            ),
            to: format(new Date(this.item.duty_date), 'yyyy-MM-dd')
          }
        }
      }
    },

    activeEvents: {
      fetchPolicy: 'no-cache',
      query: gql`
        query activeEvents {
          activeEvents {
            uuid
            title
            district_id
          }
        }
      `
    }
  },

  computed: {
    crew: get('crew/model'),

    from_place: sync('crew/model@from'),
    to_place: sync('crew/model@to'),

    stops: get('event/stops'),

    groupedEvents() {
      const activeEvents = (this.activeEvents || []).filter(
        ev => ev.district_id === this.$store.get('app/selectedDistrictId')
      )
      const archivedEvents = this.events || []
      return [
        ...(activeEvents.length ? [{ divider: true, header: 'Aktiva' }] : []),
        ...activeEvents,
        ...(archivedEvents.length
          ? [{ divider: true, header: 'Arkiverade' }]
          : []),
        ...archivedEvents
      ]
    }
  },

  methods: {
    async onCreate() {
      const isValidForm = await this.$refs.form.validate()
      if (!isValidForm) return
      this.pending = true

      this.$store.set('crew/model@event_uuid', this.model.eventUUID)

      if (this.item.crew_deviation) {
        await this.replaceCrew()
      } else {
        await this.createCrew()
      }

      this.pending = false
      this.closeModal()
      this.$emit('confirm')
    },

    async createCrew() {
      await this.$apollo.mutate({
        mutation: gql`
          mutation($payload: CreateCrewDeviationInput!) {
            createCrewDeviation(payload: $payload) {
              crew_deviation_uuid
            }
          }
        `,
        variables: {
          payload: this.crew
        }
      })
    },

    async replaceCrew() {
      await this.$apollo.mutate({
        mutation: gql`
          mutation($payload: ReplaceCrewDeviationInput!) {
            replaceCrewDeviation(payload: $payload) {
              crew_deviation_uuid
            }
          }
        `,
        variables: {
          payload: {
            delete_uuid: this.item.crew_deviation.uuid,
            payload: this.crew
          }
        }
      })
    },

    closeModal() {
      this.$store.set('linkDeviation/selectedDeviation', null)
      this.$store.commit('crew/resetModel')
    }
  }
}
</script>
